import { lazy, Suspense } from 'react';
import { ErrorNotice } from './components/ErrorNotice';
import { LoadingTrail } from './components/LoadingTrail';
import { useProfile } from './hooks/useProfile';
import { useRoute } from './hooks/useRoute';
import { HomePage } from './pages/HomePage';
import { AccountPortalPage } from './pages/AccountPortalPage';
import { StorefrontPage } from './pages/StorefrontPage';
import { ProductPage } from './pages/ProductPage';
import { PolicyPage } from './pages/PolicyPage';
import { TrailBookPage } from './pages/TrailBookPage';
import { AnimalAdventurePage } from './pages/AnimalAdventurePage';
import { AccountAwareHomePage } from './pages/AccountAwareHomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { accountAnimalTrailIdFromRoute, isShopRoute } from './lib/routes';

const ANIMAL_ROUTE = /^\/animals\/([0-9a-f-]+)\/?$/i;
const AdminPage = lazy(() =>
  import('./pages/AdminPage').then((module) => ({ default: module.AdminPage })),
);
const TrackingPage = lazy(() =>
  import('./pages/TrackingPage').then((module) => ({ default: module.TrackingPage })),
);
const PRODUCT_ROUTE = /^\/products\/([a-z0-9-]+)\/?$/;
const POLICY_ROUTES = new Set([
  'privacy',
  'terms',
  'tracking-disclosure',
  'parent-notice',
  'account-deletion',
]);

export default function App() {
  const route = useRoute();
  if (route.pathname === '/cart' || route.pathname === '/checkout/success') {
    route.navigate('/shop', true);
    return null;
  }
  if (route.pathname === '/admin' || route.pathname.startsWith('/admin/')) {
    return (
      <Suspense fallback={<LoadingTrail message="Opening the operations workspace…" />}>
        <AdminPage onBack={() => route.navigate('/')} />
      </Suspense>
    );
  }
  if (route.pathname === '/account') {
    return (
      <AccountPortalPage
        onBack={() => route.navigate('/')}
        onOpenAnimal={(entitlementId) => route.navigate(`/trails/${entitlementId}`)}
        onOpenTrailBook={() => route.navigate('/trail-book')}
      />
    );
  }
  if (route.pathname === '/trail-book') {
    return (
      <TrailBookPage
        onOpenAnimal={(entitlementId) => route.navigate(`/trails/${entitlementId}`)}
        onOpenGrownupArea={() => route.navigate('/account')}
        onShop={() => route.navigate('/shop')}
      />
    );
  }
  const accountAnimalTrailId = accountAnimalTrailIdFromRoute(route.pathname);
  if (accountAnimalTrailId) {
    return (
      <AnimalAdventurePage
        entitlementId={accountAnimalTrailId}
        onBack={() => route.navigate('/trail-book')}
        onOpenGrownupArea={() => route.navigate('/account')}
      />
    );
  }
  const animalMatch = route.pathname.match(ANIMAL_ROUTE);
  if (animalMatch) {
    return <MvpApplication pathname={route.pathname} navigate={route.navigate} />;
  }
  const productMatch = route.pathname.match(PRODUCT_ROUTE);
  if (productMatch?.[1]) {
    return <ProductPage slug={productMatch[1]} navigate={route.navigate} />;
  }
  const policyPage = route.pathname.slice(1);
  if (POLICY_ROUTES.has(policyPage)) {
    return <PolicyPage page={policyPage} navigate={route.navigate} />;
  }
  if (route.pathname === '/') {
    return <AccountAwareHomePage navigate={route.navigate} />;
  }
  if (isShopRoute(route.pathname)) return <StorefrontPage navigate={route.navigate} shopOnly />;
  return <NotFoundPage navigate={route.navigate} />;
}

function MvpApplication({ pathname, navigate }: ReturnType<typeof useRoute>) {
  const { profile, recoveryCode, loading, error, recover } = useProfile();

  if (loading) return <LoadingTrail message="Opening your trail book…" />;
  if (!profile || error) {
    return (
      <main className="fatal-screen">
        <ErrorNotice message={error ?? 'A family profile is needed to continue.'} />
      </main>
    );
  }

  const animalMatch = pathname.match(ANIMAL_ROUTE);
  if (animalMatch?.[1]) {
    return (
      <Suspense fallback={<LoadingTrail message="Drawing the trail map…" />}>
        <TrackingPage
          profileId={profile.id}
          animalId={animalMatch[1]}
          onBack={() => navigate('/trail-book')}
        />
      </Suspense>
    );
  }

  return (
    <HomePage
      profile={profile}
      recoveryCode={recoveryCode}
      onRecover={recover}
      onOpenAnimal={(animalId) => navigate(`/animals/${animalId}`)}
    />
  );
}
